import {
  EMPTY,
  type EffectiveHours,
  formatMoney,
  formatNumber,
  formatYearMonth,
  fromCents,
  routes,
  toCents,
} from '@pmai/contracts';
import { Alert, CellInput, Sheet, SheetBody, SheetHead, SheetRow, SheetTd, SheetTh, Spinner } from '@pmai/ui';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useApiQuery } from '../../shared/api/hooks';
import { Can, usePermissions } from '../../shared/auth/session';
import { CardSection } from '../../shared/card-section';
import { parseAmount } from './timesheet/sheet-edit';

const money = (cents: number) => formatMoney(fromCents(cents));
const hours = (h: number) => (h ? formatNumber(h) : '');

/**
 * "⏱️ Timesheets / Effective hours" (FR-TS-020…023): approved Jira worklogs of the project by
 * resource and month. A rate typed here recalculates the amounts for this visit only.
 */
export function EffectiveHoursSection({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const effective = useApiQuery(routes.timeOverview.effective, { params: { id: projectId } });
  const data = effective.data;
  return (
    <CardSection
      id="project.effective"
      title={t('effective.title')}
      actions={
        data && data.months.length > 0 ? (
          <span className="text-sm text-ink-muted" data-testid="effective-info">
            {t('effective.info', { resources: data.rows.length, months: data.months.length })}
          </span>
        ) : undefined
      }
    >
      {effective.isError ? (
        <Alert tone="danger">{t('effective.loadFailed')}</Alert>
      ) : !data ? (
        <Spinner label={t('common.loading')} />
      ) : data.months.length === 0 ? (
        <p className="text-sm text-ink-muted">
          {t('effective.empty')}{' '}
          <Can perm="reconciliation.manage">
            <Trans
              i18nKey="effective.emptyHint"
              components={{
                page: <Link to="/reconciliation" className="font-medium text-brand-700 hover:underline" />,
              }}
            />
          </Can>
        </p>
      ) : (
        <EffectiveTable data={data} />
      )}
    </CardSection>
  );
}

function EffectiveTable({ data }: { data: EffectiveHours }) {
  const { t } = useTranslation();
  const finance = usePermissions().includes('finance.view');
  // Typed rates — not saved; a reload brings back the resource cards' rates (FR-TS-022).
  const [rates, setRates] = useState<Record<string, string>>({});
  const rateOf = (row: EffectiveHours['rows'][number]) => rates[row.key] ?? row.rate ?? '0.00';
  const months = data.months.map(formatYearMonth);
  const span = finance ? 2 : 1;
  const amount = (h: number, rate: string) => Math.round(h * toCents(rate));
  const rowTotals = data.rows.map((r) => r.hours.reduce((a, b) => a + b, 0));
  const monthHours = data.months.map((_, i) => data.rows.reduce((a, r) => a + r.hours[i]!, 0));
  const monthCents = data.months.map((_, i) =>
    data.rows.reduce((a, r) => a + amount(r.hours[i]!, rateOf(r)), 0),
  );

  return (
    <Sheet label={t('effective.title')}>
      <SheetHead>
        <SheetRow>
          <SheetTh sticky rowSpan={2}>
            {t('timesheets.description')}
          </SheetTh>
          {finance && <SheetTh rowSpan={2}>{t('timesheets.rate')}</SheetTh>}
          {months.map((m) => (
            <SheetTh key={m} colSpan={span}>
              {m}
            </SheetTh>
          ))}
          <SheetTh colSpan={span} tone="total">
            {t('timesheets.total')}
          </SheetTh>
        </SheetRow>
        <SheetRow>
          {[...months, 'total'].map((k) => [
            <SheetTh key={`${k}-h`}>{t('timesheets.hours')}</SheetTh>,
            finance && <SheetTh key={`${k}-a`}>{t('timesheets.amount')}</SheetTh>,
          ])}
        </SheetRow>
      </SheetHead>
      <SheetBody>
        {data.rows.map((r, ri) => (
          <SheetRow key={r.key} rowKey={r.key}>
            <SheetTd sticky align="left">
              {r.name}
            </SheetTd>
            {finance && (
              <SheetTd>
                <CellInput
                  label={t('timesheets.rowRate', { row: r.name })}
                  value={rateOf(r).replace('.', ',')}
                  width="w-16"
                  onCommit={(text) => {
                    const rate = parseAmount(text);
                    if (rate !== null) setRates((all) => ({ ...all, [r.key]: rate }));
                  }}
                />
              </SheetTd>
            )}
            {r.hours.map((h, i) => [
              <SheetTd key={`h${i}`} data-col={`${i}-h`} tone="computed">
                {hours(h)}
              </SheetTd>,
              finance && (
                <SheetTd key={`a${i}`} data-col={`${i}-a`} tone="computed">
                  {h ? money(amount(h, rateOf(r))) : ''}
                </SheetTd>
              ),
            ])}
            <SheetTd tone="total" data-col="total-h">
              {formatNumber(rowTotals[ri]!)}
            </SheetTd>
            {finance && (
              <SheetTd tone="total" data-col="total-a">
                {money(r.hours.reduce((a, h) => a + amount(h, rateOf(r)), 0))}
              </SheetTd>
            )}
          </SheetRow>
        ))}
        <SheetRow kind="total" rowKey="total">
          <SheetTd sticky align="left">
            {t('timesheets.total')}
          </SheetTd>
          {finance && <SheetTd>{EMPTY}</SheetTd>}
          {monthHours.map((h, i) => [
            <SheetTd key={`h${i}`} data-col={`${i}-h`}>
              {formatNumber(h)}
            </SheetTd>,
            finance && (
              <SheetTd key={`a${i}`} data-col={`${i}-a`}>
                {money(monthCents[i]!)}
              </SheetTd>
            ),
          ])}
          <SheetTd tone="total" data-col="total-h">
            {formatNumber(monthHours.reduce((a, b) => a + b, 0))}
          </SheetTd>
          {finance && (
            <SheetTd tone="total" data-col="total-a">
              {money(monthCents.reduce((a, b) => a + b, 0))}
            </SheetTd>
          )}
        </SheetRow>
      </SheetBody>
    </Sheet>
  );
}
